import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Skeleton = ({ width = '100%', height = '20px', borderRadius = '6px' }: { width?: string, height?: string, borderRadius?: string }) => (
  <div style={{
    width, height, borderRadius,
    background: 'linear-gradient(90deg, #1a1a30 25%, #2a2a4a 50%, #1a1a30 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  }} />
);

const getCached = (key: string) => {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch { return null; }
};

const Acasa: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [profil, setProfil] = useState<any>(getCached('fx_profil'));
  const [streak, setStreak] = useState<number | null>(getCached('fx_streak'));
  const [completatAzi, setCompletatAzi] = useState<boolean>(getCached('fx_completat') ?? false);
  const [badges, setBadges] = useState<any[]>(getCached('fx_badges') ?? []);
  const [loading, setLoading] = useState(!getCached('fx_profil'));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/profil/get'),
      api.get('/tracker/streak'),
      api.get('/tracker/badges')
    ]).then(([profilRes, streakRes, badgesRes]) => {
      if (profilRes.data?.nume) {
        setProfil(profilRes.data);
        localStorage.setItem('fx_profil', JSON.stringify(profilRes.data));
      }
      if (streakRes.data?.streak !== undefined) {
        setStreak(streakRes.data.streak);
        setCompletatAzi(streakRes.data.completat_azi);
        localStorage.setItem('fx_streak', JSON.stringify(streakRes.data.streak));
        localStorage.setItem('fx_completat', JSON.stringify(streakRes.data.completat_azi));
      }
      if (badgesRes.data?.length >= 0) {
        setBadges(badgesRes.data);
        if (badgesRes.data.length > 0) {
          localStorage.setItem('fx_badges', JSON.stringify(badgesRes.data));
        }
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '820px', margin: '0 auto', fontFamily: 'DM Sans, sans-serif' }}>

        {/* Header */}
        <h1 style={{ color: '#fff', marginBottom: '0.25rem', fontSize: isMobile ? '1.5rem' : '2rem' }}>
          {profil?.nume ? `Bună, ${profil.nume}! 👋` : loading ? <Skeleton width="200px" height="2rem" /> : 'Bună! 👋'}
        </h1>
        <p style={{ color: '#8080a0', marginBottom: '1.5rem' }}>
          Asistentul tău personal pentru sănătatea coloanei vertebrale
        </p>

        {/* Banner tracker */}
        {!completatAzi && (
          <div style={{
            background: '#1a1a38', border: '1px solid #3a3a6a',
            borderRadius: '12px', padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between', gap: '0.75rem'
          }}>
            <p style={{ color: '#a0a0d8', margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>
              📊 Nu ai completat tracker-ul azi! Durează 10 secunde.
            </p>
            <button onClick={() => navigate('/tracker')} style={{
              padding: '0.5rem 1rem', borderRadius: '8px', border: 'none',
              background: '#4a4ad8', color: '#fff', cursor: 'pointer',
              fontWeight: 600, whiteSpace: 'nowrap', width: isMobile ? '100%' : 'auto'
            }}>Completează</button>
          </div>
        )}

        {/* Obiectiv */}
        {profil?.obiectiv && (
          <div style={{
            background: '#1a2a1a', border: '1px solid #2a4a2a',
            borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem'
          }}>
            <p style={{ color: '#60d090', margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>
              🎯 <strong>Obiectivul tău:</strong> {profil.obiectiv}
            </p>
          </div>
        )}

        {/* Streak */}
        <div style={{
          background: streak && streak >= 7 ? '#0f2a1a' : streak && streak >= 3 ? '#2a1a00' : '#1a1a30',
          border: `1px solid ${streak && streak >= 7 ? '#2a6a4a' : streak && streak >= 3 ? '#6a4a00' : '#2a2a4a'}`,
          borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem'
        }}>
          {streak === null ? (
            <Skeleton width="200px" height="20px" />
          ) : streak > 0 ? (
            <p style={{ color: streak >= 7 ? '#60d090' : streak >= 3 ? '#d0a040' : '#a0a0d0', margin: 0, fontSize: isMobile ? '0.9rem' : '1rem' }}>
              {streak >= 7 ? `⚡ Seria ta: ${streak} zile consecutive! Extraordinar!` :
               streak >= 3 ? `🔥 Seria ta: ${streak} zile la rând! Nu o pierde!` :
               `📅 Seria ta: ${streak} zile consecutiv`}
            </p>
          ) : (
            <p style={{ color: '#6060a0', margin: 0, fontSize: '0.9rem' }}>
              📅 Începe să completezi tracker-ul zilnic pentru a construi o serie!
            </p>
          )}
        </div>

        {/* Badges */}
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Realizările tale</h3>
        {badges.length === 0 && loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ background: '#1a1a30', borderRadius: '10px', padding: '0.75rem' }}>
                <Skeleton width="40px" height="40px" borderRadius="50%" />
                <div style={{ marginTop: '0.5rem' }}><Skeleton height="14px" /></div>
                <div style={{ marginTop: '0.25rem' }}><Skeleton height="12px" width="80%" /></div>
              </div>
            ))}
          </div>
        ) : badges.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '0.75rem', marginBottom: '1.5rem'
          }}>
            {badges.map((b, i) => (
              <div key={i} style={{
                background: '#1a1a30', border: '1px solid #2a2a4a',
                borderRadius: '10px', padding: '0.75rem', textAlign: 'center'
              }}>
                <p style={{ fontSize: '1.5rem', margin: '0 0 0.25rem' }}>{b.icon}</p>
                <p style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', margin: '0 0 0.25rem' }}>{b.titlu}</p>
                <p style={{ color: '#6060a0', fontSize: '0.75rem', margin: 0 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#6060a0', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            Completează tracker-ul zilnic pentru a câștiga badge-uri! 🏆
          </p>
        )}

        {/* Cards navigare */}
        <h3 style={{ color: '#fff', marginBottom: '1rem' }}>Ce vrei să faci?</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '0.75rem', marginBottom: '1.5rem'
        }}>
          {[
            { icon: '💬', titlu: 'Chat cu AI', desc: 'Descrie problema ta și primești sfaturi instant.', path: '/chat', color: '#1a1a38' },
            { icon: '📊', titlu: 'Tracker Durere', desc: 'Monitorizează evoluția durerii zilnic.', path: '/tracker', color: '#1a1a38' },
            { icon: '📋', titlu: 'Plan Recuperare', desc: 'Generează un plan personalizat pe săptămâni.', path: '/plan', color: '#1a1a38' },
            { icon: '👤', titlu: 'Profilul meu', desc: 'Vezi statisticile și planurile tale.', path: '/profil', color: '#1a2a1a' },
          ].map((card, i) => (
            <div key={i} onClick={() => navigate(card.path)} style={{
              background: card.color, border: '1px solid #2a2a4a',
              borderRadius: '12px', padding: isMobile ? '1rem' : '1.25rem',
              cursor: 'pointer', transition: 'transform 0.15s, border-color 0.15s'
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLDivElement).style.borderColor = '#4a4ad8';
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLDivElement).style.borderColor = '#2a2a4a';
              (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
            }}
            >
              <p style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', margin: '0 0 0.5rem' }}>{card.icon}</p>
              <p style={{ color: '#fff', fontWeight: 600, margin: '0 0 0.25rem', fontSize: isMobile ? '0.85rem' : '1rem' }}>{card.titlu}</p>
              <p style={{ color: '#6060a0', fontSize: isMobile ? '0.75rem' : '0.85rem', margin: 0 }}>{card.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: isMobile ? '0.5rem' : '1rem' }}>
          {[
            { label: 'Specializare', value: 'Spate & Sciatică' },
            { label: 'Disponibilitate', value: '24/7' },
            { label: 'Limbă', value: 'Română' }
          ].map((s, i) => (
            <div key={i} style={{
              background: '#1a1a30', border: '1px solid #2a2a4a',
              borderRadius: '10px', padding: isMobile ? '0.75rem 0.5rem' : '1rem',
              textAlign: 'center'
            }}>
              <p style={{ color: '#6060a0', fontSize: isMobile ? '0.6rem' : '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.25rem' }}>{s.label}</p>
              <p style={{ color: '#fff', fontWeight: 600, margin: 0, fontSize: isMobile ? '0.75rem' : '1rem' }}>{s.value}</p>
            </div>
          ))}
        </div>

      </div>
    </>
  );
};

export default Acasa;
